import React, { useState } from 'react';
import { Search, Loader2, Building2, User, ChevronLeft, AlertCircle, ExternalLink } from 'lucide-react';

// ---- Companies House response shapes (only the fields we render) ----
interface CompanySearchItem {
  company_number: string;
  title: string;
  company_status?: string;
  company_type?: string;
  date_of_creation?: string;
  address_snippet?: string;
}

interface CompanyProfile {
  company_name?: string;
  company_number?: string;
  company_status?: string;
  type?: string;
  date_of_creation?: string;
  sic_codes?: string[];
  registered_office_address?: Record<string, string>;
}

interface Officer {
  name?: string;
  officer_role?: string;
  appointed_on?: string;
  resigned_on?: string;
  nationality?: string;
  occupation?: string;
  date_of_birth?: { month?: number; year?: number };
  address?: Record<string, string>;
}

interface Psc {
  name?: string;
  kind?: string;
  natures_of_control?: string[];
  notified_on?: string;
  ceased_on?: string;
  nationality?: string;
  date_of_birth?: { month?: number; year?: number };
}

interface OfficerSearchItem {
  title: string;
  address_snippet?: string;
  date_of_birth?: { month?: number; year?: number };
  appointment_count?: number;
  description?: string;
}

type Mode = 'company' | 'officer';

const API = '/api/companies-house';

async function callApi<T>(params: Record<string, string>): Promise<T> {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API}?${qs}`);
  const raw = await res.text();

  let data: any;
  try {
    data = JSON.parse(raw);
  } catch {
    // The proxy returned HTML, not JSON — almost always because the /api function
    // isn't running (e.g. served by `npm start` instead of Vercel/`vercel dev`).
    throw new Error(
      "The lookup service isn't responding. If this is running locally, use `vercel dev` " +
        "(the plain dev server doesn't run the /api function). On the live site, check that " +
        'COMPANIES_HOUSE_API_KEY is set in Vercel.'
    );
  }

  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
  return data as T;
}

const fmtAddress = (a?: Record<string, string>) =>
  a
    ? [a.premises, a.address_line_1, a.address_line_2, a.locality, a.region, a.postal_code, a.country]
        .filter(Boolean)
        .join(', ')
    : '—';

const fmtDob = (d?: { month?: number; year?: number }) =>
  d?.year ? `${d.month ? String(d.month).padStart(2, '0') + '/' : ''}${d.year}` : null;

const statusPill = (status?: string) => {
  const active = status?.toLowerCase() === 'active';
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
        active
          ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400'
          : 'bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-slate-300'
      }`}
    >
      {status || 'unknown'}
    </span>
  );
};

const CompaniesHouseLookup: React.FC = () => {
  const [mode, setMode] = useState<Mode>('company');
  const [query, setQuery] = useState('');
  const [dobFilter, setDobFilter] = useState(''); // YYYY, applied client-side to officer search
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // company mode
  const [companyResults, setCompanyResults] = useState<CompanySearchItem[] | null>(null);
  const [selected, setSelected] = useState<{
    profile: CompanyProfile;
    officers: Officer[];
    psc: Psc[];
  } | null>(null);

  // officer mode
  const [officerResults, setOfficerResults] = useState<OfficerSearchItem[] | null>(null);

  const reset = () => {
    setError(null);
    setCompanyResults(null);
    setOfficerResults(null);
    setSelected(null);
  };

  const runSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;
    reset();
    setLoading(true);
    try {
      if (mode === 'company') {
        const data = await callApi<{ items?: CompanySearchItem[] }>({
          action: 'search-companies',
          q: query.trim(),
        });
        setCompanyResults(data.items || []);
      } else {
        const data = await callApi<{ items?: OfficerSearchItem[] }>({
          action: 'search-officers',
          q: query.trim(),
        });
        let items = data.items || [];
        if (dobFilter.trim()) {
          const yr = Number(dobFilter.trim());
          items = items.filter((i) => i.date_of_birth?.year === yr);
        }
        setOfficerResults(items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const openCompany = async (number: string) => {
    setError(null);
    setLoading(true);
    try {
      const [profile, officersRes, pscRes] = await Promise.all([
        callApi<CompanyProfile>({ action: 'company', number }),
        callApi<{ items?: Officer[] }>({ action: 'officers', number }),
        callApi<{ items?: Psc[] }>({ action: 'psc', number }),
      ]);
      setSelected({
        profile,
        officers: officersRes.items || [],
        psc: pscRes.items || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/5 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-orange-500" />
        <h4 className="font-bold text-slate-900 dark:text-slate-100">Live Companies House check</h4>
      </div>
      <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
        Queries the official UK register in real time. Public data only — verify before relying on it.
      </p>

      {/* mode toggle */}
      <div className="flex gap-1 mb-3 bg-white dark:bg-slate-800 rounded-lg p-1 w-fit border border-gray-200 dark:border-slate-700">
        {(['company', 'officer'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => {
              setMode(m);
              reset();
              setQuery('');
            }}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              mode === m
                ? 'bg-orange-500 text-white'
                : 'text-gray-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-500/10'
            }`}
          >
            {m === 'company' ? 'Search companies' : 'Search officers'}
          </button>
        ))}
      </div>

      {/* search form */}
      <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === 'company' ? 'Company name or number' : 'Officer / director name'}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        {mode === 'officer' && (
          <input
            value={dobFilter}
            onChange={(e) => setDobFilter(e.target.value)}
            placeholder="Birth year (optional)"
            inputMode="numeric"
            className="w-40 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        )}
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Run
        </button>
      </form>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-3 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* ---- company detail view ---- */}
      {selected ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
          <button
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 mb-3"
          >
            <ChevronLeft className="w-4 h-4" /> Back to results
          </button>

          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h5 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {selected.profile.company_name}
            </h5>
            {statusPill(selected.profile.company_status)}
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700 dark:text-slate-300 mb-4">
            <Detail label="Company number" value={selected.profile.company_number} />
            <Detail label="Type" value={selected.profile.type} />
            <Detail label="Incorporated" value={selected.profile.date_of_creation} />
            <Detail label="SIC codes" value={selected.profile.sic_codes?.join(', ')} />
            <div className="sm:col-span-2">
              <Detail label="Registered office" value={fmtAddress(selected.profile.registered_office_address)} />
            </div>
          </dl>
          <a
            href={`https://find-and-update.company-information.service.gov.uk/company/${selected.profile.company_number}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 mb-4"
          >
            Open on Companies House <ExternalLink className="w-3 h-3" />
          </a>

          <Section title={`Officers (${selected.officers.length})`}>
            {selected.officers.length === 0 ? (
              <Empty>No officers listed.</Empty>
            ) : (
              selected.officers.map((o, i) => (
                <div key={i} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="w-3.5 h-3.5 text-orange-500" />
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{o.name}</span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">{o.officer_role}</span>
                    {o.resigned_on && (
                      <span className="text-xs text-red-500">resigned {o.resigned_on}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 ml-5">
                    {[
                      fmtDob(o.date_of_birth) && `DOB ${fmtDob(o.date_of_birth)}`,
                      o.nationality,
                      o.occupation,
                      o.appointed_on && `appointed ${o.appointed_on}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              ))
            )}
          </Section>

          <Section title={`Persons with significant control (${selected.psc.length})`}>
            {selected.psc.length === 0 ? (
              <Empty>No PSC statements filed.</Empty>
            ) : (
              selected.psc.map((p, i) => (
                <div key={i} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{p.name}</span>
                    {p.ceased_on && <span className="text-xs text-red-500">ceased {p.ceased_on}</span>}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {[
                      fmtDob(p.date_of_birth) && `DOB ${fmtDob(p.date_of_birth)}`,
                      p.nationality,
                      ...(p.natures_of_control || []).map((n) => n.replace(/-/g, ' ')),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              ))
            )}
          </Section>
        </div>
      ) : (
        <>
          {/* ---- company results list ---- */}
          {companyResults && (
            <ResultList
              empty={companyResults.length === 0}
              emptyText="No companies matched."
            >
              {companyResults.map((c) => (
                <button
                  key={c.company_number}
                  onClick={() => openCompany(c.company_number)}
                  className="w-full text-left px-3 py-2.5 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-0"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{c.title}</span>
                    {statusPill(c.company_status)}
                    <span className="text-xs text-gray-400">#{c.company_number}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {[c.company_type, c.date_of_creation && `inc. ${c.date_of_creation}`, c.address_snippet]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </button>
              ))}
            </ResultList>
          )}

          {/* ---- officer results list ---- */}
          {officerResults && (
            <ResultList
              empty={officerResults.length === 0}
              emptyText="No officers matched (try without the birth-year filter)."
            >
              {officerResults.map((o, i) => (
                <div
                  key={i}
                  className="px-3 py-2.5 border-b border-gray-100 dark:border-slate-700 last:border-0"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="w-3.5 h-3.5 text-orange-500" />
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{o.title}</span>
                    {fmtDob(o.date_of_birth) && (
                      <span className="text-xs text-gray-500 dark:text-slate-400">DOB {fmtDob(o.date_of_birth)}</span>
                    )}
                    {typeof o.appointment_count === 'number' && (
                      <span className="text-xs text-gray-400">{o.appointment_count} appointments</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {[o.description, o.address_snippet].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ))}
            </ResultList>
          )}
        </>
      )}
    </div>
  );
};

// ---- small presentational helpers ----
const Detail: React.FC<{ label: string; value?: string }> = ({ label, value }) =>
  value ? (
    <div>
      <span className="text-gray-400 dark:text-slate-500">{label}: </span>
      <span>{value}</span>
    </div>
  ) : null;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mt-4">
    <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-1">{title}</p>
    {children}
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm text-gray-400 dark:text-slate-500 italic">{children}</p>
);

const ResultList: React.FC<{ empty: boolean; emptyText: string; children: React.ReactNode }> = ({
  empty,
  emptyText,
  children,
}) =>
  empty ? (
    <Empty>{emptyText}</Empty>
  ) : (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 max-h-96 overflow-y-auto">
      {children}
    </div>
  );

export default CompaniesHouseLookup;
